import { useEffect, useRef, useState } from 'react';
import { SHIFT_ACCOUNT_PERMISSION_KEYS } from 'shared-models';
import type { ShiftAccount, ShiftAccountDirectory, ShiftAccountPatch, ShiftAccountState } from 'shared-models';
import { useShiftEvents } from '../../contexts/ShiftEventsProvider';
import { shiftEmployeesService } from '../../services/employees/shift-employees.service';

type AccountChanges = Omit<ShiftAccountPatch,'expectedRevision'>;
export function useAccountManagementController(enabled:boolean,actorId:number | undefined) {
  const [directory,setDirectory] = useState<ShiftAccountDirectory | null>(null);
  const [state,setState] = useState<ShiftAccountState | 'all'>('all');
  const [offset,setOffset] = useState(0);
  const [selected,setSelected] = useState<ShiftAccount | null>(null);
  const [draft,setDraft] = useState<ShiftAccountPatch | null>(null);
  const [confirmed,setConfirmed] = useState(false);
  const [isLoading,setIsLoading] = useState(false);
  const [isSaving,setIsSaving] = useState(false);
  const [error,setError] = useState<string | null>(null);
  const [membershipChanged,setMembershipChanged] = useState(false);
  const [reload,setReload] = useState(0);
  const lock = useRef(false);
  const latest = useRef(new Map<number,ShiftAccount>());
  const ready = useShiftEvents(event => {
    if (!enabled) return;
    if (event.type==='sse.resync') {setReload(current => current+1);return;}
    if (event.type!=='account.upserted' || (latest.current.get(event.account.shiftUserId)?.revision ?? 0)>=event.account.revision) return;
    latest.current.set(event.account.shiftUserId,event.account);
    setSelected(current => current?.shiftUserId===event.account.shiftUserId ? event.account : current);
    setDirectory(current => {
      if (!current) return current;
      return {...current,accounts:current.accounts.map(account => account.shiftUserId===event.account.shiftUserId ? event.account : account)
        .filter(account => state==='all' || account.state===state)};
    });
    setMembershipChanged(true);
  });
  useEffect(() => {
    if (!enabled || !ready) return;
    let active = true;
    setIsLoading(true);setError(null);setDirectory(null);setMembershipChanged(false);
    void Promise.resolve().then(async () => {
      if (!active) return;
      try {
        const snapshot = await shiftEmployeesService.listAccounts(offset,state);
        if (!active) return;
        const accounts = snapshot.accounts.map(account => {
          const overlay = latest.current.get(account.shiftUserId);
          if (overlay && overlay.revision>account.revision) return overlay;
          latest.current.set(account.shiftUserId,account);
          return account;
        }).filter(account => state==='all' || account.state===state);
        setDirectory({...snapshot,accounts});
      } catch {if (active) setError('לא ניתן לטעון את חשבונות SHIFT.');}
      finally {if (active) setIsLoading(false);}
    });
    return () => {active=false;};
  },[enabled,ready,offset,state,reload]);

  const stale = !!draft && draft.expectedRevision!==selected?.revision;
  const busy = isLoading || isSaving || draft!==null;
  const onChange = (change:AccountChanges) => {
    if (!selected || selected.shiftUserId===actorId || lock.current) return;
    const changes = change.role==='worker' ? {...change,...Object.fromEntries(SHIFT_ACCOUNT_PERMISSION_KEYS.map(key => [key,false]))} : change;
    setDraft(current => {
      const next = {...current,...changes,expectedRevision:current?.expectedRevision ?? selected.revision};
      for (const key of ['state','role',...SHIFT_ACCOUNT_PERMISSION_KEYS] as const) if (next[key]===selected[key]) delete next[key];
      return Object.keys(next).length>1 ? next : null;
    });
    setConfirmed(false);setError(null);
  };
  const discard = () => {setDraft(null);setConfirmed(false);setError(null);};
  const save = async () => {
    if (lock.current || !selected || !draft || stale || !confirmed || selected.shiftUserId===actorId) return;
    lock.current=true;setIsSaving(true);setError(null);
    try {
      await shiftEmployeesService.patchAccount(selected.shiftUserId,draft);
      discard();setSelected(null);
    } catch {setError('שמירת החשבון נכשלה. השינויים נשמרו במסך.');}
    finally {lock.current=false;setIsSaving(false);}
  };
  return {directory,state,offset,selected,draft,confirmed,isLoading,isSaving,error,membershipChanged,stale,busy,
    onChange,discard,save,setConfirmed,
    select:(account:ShiftAccount) => {if (!busy && !lock.current) {setSelected(account);discard();}},
    changeState:(value:ShiftAccountState | 'all') => {if (!busy && !lock.current) {setState(value);setOffset(0);setSelected(null);}},
    changePage:(value:number) => {if (!busy && !lock.current) {setOffset(value);setSelected(null);}},
    refresh:() => {if (!busy && !lock.current) {setSelected(null);setReload(current => current+1);}},
  };
}